// ABOUTME: Shared styled button component for FlipYourCoin and MakeItRight buttons
// ABOUTME: Ensures consistent width, height, colors, fonts, and responsive icon sizing

import { Button } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import naluV1Theme from '../../theme/theme.nalu-v1';

export const StyledActionButton = styled(Button)(({ theme }) => {
  const isNaluV1 = theme.logoSrc === naluV1Theme.logoSrc;
  return {
  // Responsive width - flexible on mobile, fixed on desktop
  minWidth: 0, // Allow flex to control width on mobile
  flex: '1 1 0', // Equal flex basis so buttons are truly equal width
  maxWidth: '100%', // Prevent overflow

  // Compact padding on mobile/tablet (below md breakpoint)
  padding: '8px 14px',

  // Desktop layout (md breakpoint = 900px and above)
  [theme.breakpoints.up('md')]: {
    padding: '10px 24px',
    minWidth: '160px',
    flex: 'none',
    gap: '8px',
  },

  // Consistent dimensions
  height: '48px', // Fixed height for perfect alignment

  // Typography consistency
  // fontSize matches the button variant's own baseline; not a Typography override
  fontSize: '14px',
  fontWeight: isNaluV1 ? 800 : 600,
  textTransform: 'none', // Preserve text casing

  // Layout
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  whiteSpace: 'normal', // Allow text wrapping on narrow buttons

  // Visual styling
  borderRadius: isNaluV1 ? '16px' : `${Number(theme.shape.borderRadius) * (2 / 3)}px`, // = 8px

  // Responsive icon sizing
  '& .MuiButton-startIcon': {
    // Icon glyph sizing via fontSize, not a Typography text override (mobile icon size)
    fontSize: isNaluV1 ? '44px' : '18px',
    marginLeft: 0,
    marginRight: '4px',

    // Desktop icon and spacing
    [theme.breakpoints.up('md')]: {
      // Icon glyph sizing via fontSize, not a Typography text override
      fontSize: isNaluV1 ? '44px' : '24px',
      marginRight: '8px',
    },
  },

  // Outlined variant styles (white middle with colored border under default/alt; nalu-v1
  // matches the mockup's cream card with a thin colored border, no shadow)
  '&.MuiButton-outlined': {
    backgroundColor: isNaluV1 ? theme.palette.background.default : alpha(theme.palette.common.white, 0.9),
    borderWidth: isNaluV1 ? 1.5 : 2,
    borderColor: isNaluV1 ? theme.palette.primary.light : theme.palette.primary.main,
    color: isNaluV1 ? theme.palette.primary.dark : theme.palette.primary.main,

    '&:hover': {
      backgroundColor: isNaluV1 ? theme.palette.background.paper : alpha(theme.palette.primary.main, 0.08),
      borderWidth: isNaluV1 ? 1.5 : 2,
      transform: 'translateY(-1px)',
      boxShadow: isNaluV1 ? 'none' : `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
    },
  },

  // Contained variant styles (solid background)
  '&.MuiButton-contained': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,

    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
    },
  },
  
  // Active state for both variants
  '&:active': {
    transform: 'translateY(0px) scale(0.98)',
  },
  
  // Smooth transitions
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

  // Disabled state consistency
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
    opacity: 0.6,
  },
  };
});

// Shared sx overrides for icon-only mode on mobile (matches top-3 behavior button style).
// isNaluV1 must be passed in by the caller (a plain exported object can't see the active
// theme) — under nalu-v1 this matches BehaviorIconButton's fixed 72px square dimensions.
export const mobileIconOnlySx = (isNaluV1: boolean) => ({
  flex: 'none',
  minWidth: isNaluV1 ? { xs: 60, sm: 72 } : { xs: 70, sm: 80 },
  minHeight: isNaluV1 ? { xs: 60, sm: 72 } : { xs: 55, sm: 65 },
  height: 'auto',
  borderRadius: isNaluV1 ? '18px' : 3,
  px: { xs: 1.5, sm: 2 },
  py: { xs: 1, sm: 1.5 },
  '& .MuiButton-startIcon': {
    fontSize: isNaluV1 ? 44 : { xs: 28, sm: 32 },
    marginRight: 0,
    marginLeft: 0,
  },
});